import { DndContext, DragOverlay, PointerSensor, TouchSensor, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Icon } from "../components/Icon.tsx";
import { Empty, ErrorView, Field, Loading, Page, Sheet, useAddParam, useFail, useToast } from "../components/ui.tsx";
import { api, ApiError, isQueued } from "../lib/api.ts";
import { dateShort, label, money } from "../lib/format.ts";
import { useMe } from "../lib/session.tsx";

export function Pipelines() {
  const [sp, setSp] = useSearchParams();
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useFail();
  const pipeline = (sp.get("pipeline") as "seller" | "buyer") ?? "seller";
  const view = sp.get("view") ?? "board";
  const mine = sp.get("mine") === "1";
  const pipes = useQuery({ queryKey: ["pipelines"], queryFn: () => api.get<any[]>("/pipelines"), staleTime: 3600_000 });
  const opps = useQuery({ queryKey: ["opportunities", pipeline, mine], queryFn: () => api.get<any[]>(`/opportunities?pipeline=${pipeline}${mine ? "&assigned=me" : ""}`) });
  const [dragging, setDragging] = useState<any>(null);
  const [moving, setMoving] = useState<any>(sp.get("open") ? { id: sp.get("open") } : null);
  const [lostFor, setLostFor] = useState<{ card: any; stage: any } | null>(null);
  const [adding, setAdding] = useState(false);
  useAddParam(() => setAdding(true));
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }));
  const set = (k: string, v: string | null) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); n.delete("open"); setSp(n, { replace: true }); };

  const stages: any[] = pipes.data?.find((p) => p.key === pipeline)?.stages ?? [];
  const cards = opps.data ?? [];

  const move = async (card: any, stage: any, lostReason?: string) => {
    if (card.stage_id === stage.id) return;
    if (stage.outcome === "lost" && !lostReason) { setLostFor({ card, stage }); return; }
    // Show it in its new column straight away; put it back if the server says no.
    const key = ["opportunities", pipeline, mine];
    const before = qc.getQueryData<any[]>(key);
    qc.setQueryData<any[]>(key, (old) => old?.map((o) => (o.id === card.id ? { ...o, stage_id: stage.id, stage_key: stage.key, stage_name: stage.name, days_in_stage: 0, stale: false } : o)));
    try {
      const r = await api.patch(`/opportunities/${card.id}`, { stage_id: stage.id, ...(lostReason ? { lost_reason: lostReason } : {}) }, card.version, { offline: `Move ${card.person_name} to ${stage.name}` });
      toast(isQueued(r) ? `Moved on this phone; it'll sync when you're back in range` : `${card.person_name}: ${stage.name}`);
      if (!isQueued(r)) qc.invalidateQueries({ queryKey: ["opportunities"] });
    } catch (e) {
      qc.setQueryData(key, before);
      if (e instanceof ApiError && e.status === 412) { toast("Someone else moved this deal first. Showing their change.", true); qc.invalidateQueries({ queryKey: ["opportunities"] }); }
      else fail(e);
    }
  };

  const onDragEnd = (e: DragEndEvent) => {
    setDragging(null);
    const card = cards.find((c) => c.id === e.active.id);
    const stage = stages.find((s) => s.id === e.over?.id);
    if (card && stage) move(card, stage);
  };

  return (
    <Page title="Pipelines" wide actions={<button className="iconbtn" aria-label="New deal" onClick={() => setAdding(true)}><Icon name="plus" /></button>}>
      <div className="chips">
        <button className={"chip" + (pipeline === "seller" ? " on" : "")} onClick={() => set("pipeline", "seller")}>Sellers</button>
        <button className={"chip" + (pipeline === "buyer" ? " on" : "")} onClick={() => set("pipeline", "buyer")}>Buyers</button>
        <span style={{ width: 8 }} />
        <button className={"chip" + (mine ? " on" : "")} onClick={() => set("mine", mine ? null : "1")}>Mine</button>
        <button className={"chip" + (view === "list" ? " on" : "")} onClick={() => set("view", view === "list" ? null : "list")}>{view === "list" ? "Board view" : "List view"}</button>
      </div>
      {pipes.isLoading || opps.isLoading ? <Loading /> : opps.error ? <ErrorView error={opps.error} /> : view === "list" ? (
        <ListView cards={cards} onOpen={setMoving} />
      ) : (
        <DndContext sensors={sensors} onDragStart={(e) => setDragging(cards.find((c) => c.id === e.active.id))} onDragEnd={onDragEnd} onDragCancel={() => setDragging(null)}>
          <div className="board">
            {stages.map((s) => {
              const col = cards.filter((c) => c.stage_id === s.id);
              const total = col.reduce((n, c) => n + (c.value_cents ?? 0), 0);
              return (
                <Column key={s.id} stage={s} count={col.length} total={total}>
                  {col.length === 0 ? <div className="dim" style={{ fontSize: 13, padding: "6px 4px" }}>{s.outcome === "open" ? "Drop a deal here" : "Nothing in the last 30 days"}</div>
                    : col.map((c) => <Card key={c.id} card={c} onOpen={() => setMoving(c)} />)}
                </Column>
              );
            })}
          </div>
          <DragOverlay dropAnimation={null}>{dragging && <div className="card overlay"><CardBody card={dragging} /></div>}</DragOverlay>
        </DndContext>
      )}
      <div className="pad dim" style={{ fontSize: 12.5, marginBottom: 12 }}>
        Hold and drag a card to move it, or tap it. {pipeline === "seller" ? "After Form 6 is signed, deals follow Rex by themselves (on the market, under contract, sold)." : "A buyer has one deal; each listing they enquire on joins it."}
      </div>
      {moving && <DealSheet id={moving.id} stages={stages} onMove={(card, s) => { setMoving(null); move(card, s); }} onClose={() => { setMoving(null); if (sp.get("open")) set("open", null); }} />}
      {lostFor && <LostSheet name={lostFor.card.person_name} onClose={() => setLostFor(null)} onSave={(r) => { const l = lostFor; setLostFor(null); move(l.card, l.stage, r); }} />}
      {adding && <NewDealSheet pipeline={pipeline} meId={me.user.id} onClose={() => setAdding(false)} onDone={() => qc.invalidateQueries({ queryKey: ["opportunities"] })} />}
    </Page>
  );
}

function Column({ stage, count, total, children }: { stage: any; count: number; total: number; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });
  return (
    <div ref={setNodeRef} className={"col" + (isOver ? " over" : "") + (stage.outcome === "lost" ? " lost" : stage.outcome === "won" ? " won" : "")}>
      <div className="col-head">
        {stage.outcome === "open" && <span className="sq" />}{stage.name}
        <span className="n num">{total > 0 && <span className="val">{money(total, { short: true })} · </span>}{count}</span>
      </div>
      <div className="col-body">{children}</div>
    </div>
  );
}

function Card({ card, onOpen }: { card: any; onOpen: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: card.id });
  return (
    <div ref={setNodeRef} className={"card" + (isDragging ? " dragging" : "")} {...listeners} {...attributes} onClick={onOpen} role="button" aria-label={`${card.title}. Tap for options`}>
      <CardBody card={card} />
    </div>
  );
}

function CardBody({ card }: { card: any }) {
  return (
    <>
      <div className="t">{card.person_name}</div>
      {(card.address ?? (card.title !== card.person_name ? card.title : null)) && <div className="p">{card.address ?? card.title}</div>}
      {card.interests.length > 0 && (
        <div className="ints">
          {card.interests.slice(0, 4).map((i: any) => <div key={i.id} className={"int" + (["offered", "under_contract", "inspected"].includes(i.status) ? " hot" : "")}><span className="sq" />{i.address.split(",")[0]} · {label(i.status)}</div>)}
          {card.interests.length > 4 && <div className="int">+{card.interests.length - 4} more</div>}
        </div>
      )}
      <div className="ft">
        <span>{card.value_cents ? money(card.value_cents, { short: true }) : ""}{card.assigned_name ? ` · ${card.assigned_name.split(" ")[0]}` : ""}</span>
        <span className={card.stale ? "stale" : ""}>{card.stage_key === "nurture" && card.next_follow_up_on ? `Check in ${dateShort(card.next_follow_up_on)}` : card.lost_reason ? card.lost_reason : `${card.days_in_stage}d`}</span>
      </div>
    </>
  );
}

function ListView({ cards, onOpen }: { cards: any[]; onOpen: (c: any) => void }) {
  if (!cards.length) return <Empty>No deals.</Empty>;
  return (
    <div className="list">
      {cards.map((c) => (
        <button key={c.id} className="row" onClick={() => onOpen(c)}>
          <div className="main-col"><div className="title">{c.person_name}</div><div className="sub">{c.stage_name} · {c.address ?? c.title}{c.value_cents ? ` · ${money(c.value_cents, { short: true })}` : ""}</div></div>
          <span className={"when" + (c.stale ? " danger" : "")}>{c.days_in_stage}d</span>
        </button>
      ))}
    </div>
  );
}

function DealSheet({ id, stages, onMove, onClose }: { id: string; stages: any[]; onMove: (card: any, s: any) => void; onClose: () => void }) {
  const q = useQuery({ queryKey: ["opportunity", id], queryFn: () => api.get(`/opportunities/${id}`) });
  const o = q.data;
  const st = stages.length ? stages : [];
  return (
    <Sheet title={o?.title ?? "Deal"} onClose={onClose}>
      {!o ? <Loading /> : (
        <>
          <div className="badges" style={{ marginBottom: 12 }}>
            <span className="badge white">{o.stage_name}</span>{o.value_cents && <span className="badge">{money(o.value_cents)}</span>}{o.assigned_name && <span className="badge">{o.assigned_name}</span>}
            {o.closed_at && <span className="badge">{o.outcome === "won" ? "Won" : "Closed"}</span>}
          </div>
          <Link to={`/people/${o.person_id}`} className="btn ghost block" onClick={onClose}><Icon name="user" />Open {o.person_name}</Link>
          <div className="section-title" style={{ padding: "18px 0 8px" }}><span className="sq" />Move to</div>
          <div className="chips" style={{ flexWrap: "wrap", padding: 0 }}>
            {(st.length ? st : []).map((s) => <button key={s.id} className={"chip" + (s.id === o.stage_id ? " on" : "")} onClick={() => onMove(o, s)} disabled={s.id === o.stage_id}>{s.name}</button>)}
          </div>
          {o.interests.length > 0 && (<>
            <div className="section-title" style={{ padding: "18px 0 8px" }}><span className="sq" />Listings on this deal</div>
            {o.interests.map((i: any) => <div key={i.id} className="row" style={{ padding: "8px 0" }}><div className="main-col"><div className="title">{i.address}</div><div className="sub">{label(i.status)}{i.level ? ` · ${label(i.level)}` : ""}</div></div></div>)}
          </>)}
          <div className="section-title" style={{ padding: "18px 0 8px" }}><span className="sq" />History</div>
          {o.history.map((h: any) => <div key={h.id} className="sub" style={{ padding: "4px 0", fontSize: 13.5 }}>{dateShort(h.moved_at.slice(0, 10))} · {h.from_stage ? `${h.from_stage} → ` : ""}{h.to_stage}{h.moved_by_name ? ` · ${h.moved_by_name}` : " · Rex"}{h.reason ? ` · ${h.reason}` : ""}</div>)}
        </>
      )}
    </Sheet>
  );
}

function LostSheet({ name, onSave, onClose }: { name: string; onSave: (reason: string) => void; onClose: () => void }) {
  const [r, setR] = useState("");
  const common = ["Listed with another agent", "Bought elsewhere", "Not selling now", "Finance fell through", "Stopped replying"];
  return (
    <Sheet title={`Why was ${name} lost?`} onClose={onClose} foot={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn" disabled={!r.trim()} onClick={() => onSave(r)}>Mark lost</button></>}>
      <div className="chips" style={{ flexWrap: "wrap", padding: "0 0 12px" }}>{common.map((c) => <button key={c} className={"chip" + (r === c ? " on" : "")} onClick={() => setR(c)}>{c}</button>)}</div>
      <Field label="Reason"><input className="input" value={r} onChange={(e) => setR(e.target.value)} /></Field>
      <div className="muted" style={{ fontSize: 13 }}>Not ready yet? Use Nurture instead: it stays open with a check-in every 90 days.</div>
    </Sheet>
  );
}

function NewDealSheet({ pipeline, meId, onClose, onDone }: { pipeline: string; meId: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const fail = useFail();
  const [q, setQ] = useState("");
  const [person, setPerson] = useState<any>(null);
  const [listingId, setListingId] = useState("");
  const [value, setValue] = useState("");
  const people = useQuery({ queryKey: ["people-pick", q], queryFn: () => api.get(`/people?q=${encodeURIComponent(q)}&limit=8`), enabled: q.length >= 2 });
  const listings = useQuery({ queryKey: ["listings", "current"], queryFn: () => api.get<any[]>("/listings?state=current") });
  const save = async () => {
    try {
      const r: any = await api.post("/opportunities", { pipeline, person_id: person.id, listing_id: listingId || undefined, value_cents: value ? Number(value) * 100 : undefined, assigned_user_id: meId });
      toast(r.added_to_existing ? `Added to ${person.display_name}'s existing buyer deal` : "Deal created");
      onDone(); onClose();
    } catch (e) { fail(e); }
  };
  return (
    <Sheet title={pipeline === "seller" ? "New seller deal" : "New buyer deal"} onClose={onClose} foot={<><button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn" disabled={!person || (pipeline === "seller" && !listingId)} onClick={save}>Create</button></>}>
      {person ? <div className="row" style={{ padding: "0 0 12px" }}><div className="main-col"><div className="title">{person.display_name}</div></div><button className="btn ghost sm" onClick={() => setPerson(null)}>Change</button></div> : (
        <>
          <Field label="Who"><input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people" /></Field>
          {(people.data?.rows ?? []).map((p: any) => <button key={p.id} className="row" onClick={() => setPerson(p)}><div className="main-col"><div className="title">{p.display_name}</div></div></button>)}
        </>
      )}
      <Field label={pipeline === "seller" ? "Listing (a seller deal needs one; prospects come from their property page)" : "Listing they enquired on (optional)"}>
        <select className="select" value={listingId} onChange={(e) => setListingId(e.target.value)}>
          <option value="">{pipeline === "seller" ? "Pick a listing" : "None yet"}</option>
          {(listings.data ?? []).map((l) => <option key={l.id} value={l.id}>{l.address_display}</option>)}
        </select>
      </Field>
      <Field label={pipeline === "seller" ? "Expected price ($)" : "Budget ($)"}><input className="input" inputMode="numeric" value={value} onChange={(e) => setValue(e.target.value.replace(/\D/g, ""))} /></Field>
    </Sheet>
  );
}
